import * as React from 'react';
import { Formik, Form } from 'formik';
import { NavLink, Link, useHistory } from 'react-router-dom';

import { isEmpty } from 'lodash';
import axios from 'axios';
import { STATIC_STORAGE_URL } from '@nexxt/common/constants';
import {
    uppercaseFirstLetter,
    getQueryString
} from '@nexxt/common/services/TextService';

import NotifyHOC from './HOC/Notifications';
import TextField from './TextField';
import AuthContext from './AuthContext';
import { User, USER_ACCESS_TYPES, USER_STATUS } from '../types/User';
import { LOGIN } from '../constants/routes';
import {
    getSignupValidationSchema,
    getSignupWithInvitationValidationSchema
} from '../utils/validation';
import Loader from './Loader';
import PageNotFound from './PageNotFound';
import Logo from '../assets/SVG/nexxt-logo';
import { getFeaturedRapidSolution, loginProcess } from '../utils/authProcess';
import Warning from './Warning';
import Splash from './Splash';
import { SPLASH_CONTENT } from '../utils/constants';

interface SignupProps {
    changePath: (path: string) => void;
    toastNotification: any;
    user: User;
    location: any;
}
interface SignupState {
    stage: string;
    email: string;
    firstName: string;
    lastName: string;
    company: string;
    jobTitle: string;
    invitationCode: string;
    isUnvalid: boolean;
    isSignupRequesting: boolean;
    invited: boolean;
    textFocused: string;
}
const STAGE = { FIRST: 'FIRST', SECOND: 'SECOND' };
const LOGIN_GIF = {
    HELLO: 'log-in_hello_white',
    SUCESS: 'log-in_success_white',
    INPUT: 'log-in_email_white',
    FAIL: 'log-in_fail_white',
    PASSWORD: 'log-in_idle_white'
};

const parseUserData = (u: any): User => {
    return {
        id: u.UserID || -1,
        email: u.Email || '',
        profilePicture: u.ProfilePictureURL || '',
        lastName: u.LastName || '',
        firstName: u.FirstName || '',
        company: u.Company || '',
        accessType: u.AccessType || USER_ACCESS_TYPES.DEFAULT,
        jobTitle: u.JobTitle || '',
        accountUrl: '',
        createdDate: u.CreatedDate,
        status: u.InvitationCode?.length
            ? USER_STATUS.INVITED
            : USER_STATUS.ACTIVE
    } as User;
};

async function validateInvitationCode(path): Promise<User> {
    const url = `${process.env.INCA_SERVER_URL}/auth/invitation/validate?code=${path}`;
    const response = await axios.post(url);
    const validatedUser = parseUserData(response.data);
    return validatedUser;
}

function getCode(data) {
    if (!data) return null;

    return data.split('?code=')[1];
}

const authAlertMessage = {
    CODE_INVALID: `Your validation code is incorrect. Please contact ${process.env.CONTACT_EMAIL_ADDRESS}!`,
    SUCCESS_REGISTERED: 'Looking good! Now you can login.'
};

const SignUp = (props: SignupProps) => {
    const [stage, setStage] = React.useState(STAGE.FIRST);
    const [email, setEmail] = React.useState('');
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [company, setCompany] = React.useState('');
    const [jobTitle, setJobTitle] = React.useState('');
    const [isSignupRequesting, setIsSignupRequesting] = React.useState(false);
    const [invited, setInvited] = React.useState(false);
    const [invitationCode, setInvitationCode] = React.useState('');
    const [isUnvalid, setIsUnvalid] = React.useState(false);
    const [textFocused, setTextFocused] = React.useState(LOGIN_GIF.HELLO);
    const [splashIndex, setSplashIndex] = React.useState(
        Math.round(Math.random() * (SPLASH_CONTENT.length - 1))
    );
    const { firebaseAuth } = React.useContext(AuthContext);
    const sourceApp = getQueryString('ref');
    const rapidSolution = getFeaturedRapidSolution();
    const history = useHistory();

    const redirect = () => {
        const redirectTo = sourceApp
            ? sourceApp
            : process.env.PORTAL_URL + rapidSolution;
        window.location.href = redirectTo;
    };

    React.useEffect(() => {
        document.title = 'inca | signup';
        const code = getCode(props.location.search);
        if (code) {
            setInvitationCode(code);
            setInvited(true);
            validateCode(code);
        }
    }, []);

    const switchStage = () => {
        setStage(stage === STAGE.FIRST ? STAGE.SECOND : STAGE.FIRST);
    };

    const onFocusTextField = (txtFocused) => {
        setTextFocused(txtFocused);
    };

    /**
     * Validate invitation code
     */
    const validateCode = async (invitationCode) => {
        setIsUnvalid(false);
        try {
            setIsSignupRequesting(true);
            const validatedUser = await validateInvitationCode(invitationCode);
            setIsSignupRequesting(false);
            setEmail(validatedUser.email);
            setFirstName(validatedUser.firstName);
            setLastName(validatedUser.lastName);
            setCompany(validatedUser.company);
            setJobTitle(validatedUser.jobTitle);
        } catch (error) {
            setIsSignupRequesting(false);
            setInvited(false);
            setIsUnvalid(true);

            props.toastNotification(authAlertMessage.CODE_INVALID);
        }
    };

    const handleSubmit = async (values: any) => {
        onFocusTextField(LOGIN_GIF.SUCESS);
        signUpUser(values);
    };

    const signUpUser = async (values) => {
        setIsSignupRequesting(true);
        try {
            const userCredentials =
                await firebaseAuth.createUserWithEmailAndPassword(
                    values.email,
                    values.password
                );

            const firebaseUid = userCredentials.user.uid;

            if (invited) {
                await axios.post(`${process.env.INCA_SERVER_URL}/auth/signup`, {
                    ...values,
                    firebaseUid,
                    invitationCode
                });
            } else {
                await axios.post(`${process.env.INCA_SERVER_URL}/auth/signup`, {
                    ...values,
                    firebaseUid
                });
            }

            await loginProcess(userCredentials);

            setIsSignupRequesting(false);
            redirect();
            props.toastNotification.success(
                authAlertMessage.SUCCESS_REGISTERED
            );
        } catch (error) {
            console.log('error:', error);
            const errorMessage = error.message;
            props.toastNotification.error(errorMessage);
            setIsSignupRequesting(false);
        }
    };

    const isLoggedIn = props.user;
    if (isLoggedIn && isLoggedIn.firstName) {
        history.push(LOGIN + rapidSolution);
    }

    if (isUnvalid) {
        return (
            <PageNotFound message="Your invitation code is invalid or expired." />
        );
    }

    const currentDateTime = new Date();
    const hours = currentDateTime.getHours();
    const welcomeText =
        hours < 24 && hours > 16
            ? 'Good evening!'
            : hours >= 12
            ? 'Good afternoon!'
            : 'Good morning!';

    return (
        <div className="min-h-screen grid grid-cols-12">
            <div className="hidden lg:flex lg:col-span-6 xl:col-span-8 max-h-screen">
                <Splash index={splashIndex} />
            </div>
            {isSignupRequesting ? <Loader /> : null}
            <div className="col-span-12 lg:col-span-6 xl:col-span-4">
                <div className="auth--container">
                    <div className="auth--wrapper">
                        <div className="auth--header flex flex-col items-center mb-2">
                            {/* TODO: REPLACE LINK 👇 */}
                            <NavLink exact={true} to={LOGIN + rapidSolution}>
                                <Logo />
                            </NavLink>
                            <p className="auth--header--title mb-1">
                                {welcomeText}
                                {firstName &&
                                    `, ${uppercaseFirstLetter(firstName)}.`}
                            </p>
                            <p className="auth--header--subtitle">
                                Let&apos;s get you started.
                            </p>
                        </div>
                        <div className="auth--body">
                            {invited && !email ? null : (
                                <Formik
                                    initialValues={{
                                        lastName,
                                        firstName,
                                        email,
                                        company,
                                        jobTitle,
                                        password: '',
                                        confirmPassword: '',
                                        url: ''
                                    }}
                                    validationSchema={
                                        invited
                                            ? getSignupWithInvitationValidationSchema
                                            : getSignupValidationSchema
                                    }
                                    onSubmit={(values, actions) => {
                                        handleSubmit(values);
                                    }}
                                    enableReinitialize
                                    render={({
                                        values,
                                        touched,
                                        handleChange,
                                        handleBlur,
                                        errors
                                    }) => (
                                        <Form className="registration-forms">
                                            {stage === STAGE.FIRST ? (
                                                <>
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <TextField
                                                                type="text"
                                                                name="firstName"
                                                                value={
                                                                    values.firstName
                                                                }
                                                                onChange={
                                                                    handleChange
                                                                }
                                                                onBlur={
                                                                    handleBlur
                                                                }
                                                                onFocus={() =>
                                                                    onFocusTextField(
                                                                        LOGIN_GIF.INPUT
                                                                    )
                                                                }
                                                                placeholder="First Name"
                                                                label="First Name"
                                                                errorMessage={
                                                                    errors.firstName
                                                                }
                                                                touchedValue={
                                                                    touched.firstName
                                                                }
                                                            />
                                                        </div>
                                                        <div className="col-md-6">
                                                            <TextField
                                                                type="text"
                                                                name="lastName"
                                                                value={
                                                                    values.lastName
                                                                }
                                                                onChange={
                                                                    handleChange
                                                                }
                                                                onBlur={
                                                                    handleBlur
                                                                }
                                                                onFocus={() =>
                                                                    onFocusTextField(
                                                                        LOGIN_GIF.INPUT
                                                                    )
                                                                }
                                                                placeholder="Last Name"
                                                                label="Last Name"
                                                                errorMessage={
                                                                    errors.lastName
                                                                }
                                                                touchedValue={
                                                                    touched.lastName
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                    <TextField
                                                        type="text"
                                                        name="email"
                                                        value={
                                                            invited
                                                                ? email
                                                                : values.email
                                                        }
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        onFocus={() =>
                                                            onFocusTextField(
                                                                LOGIN_GIF.INPUT
                                                            )
                                                        }
                                                        placeholder="Email"
                                                        label="Email"
                                                        disabled={invited}
                                                        errorMessage={
                                                            errors.email
                                                        }
                                                        touchedValue={
                                                            touched.email
                                                        }
                                                    />
                                                    <TextField
                                                        type="text"
                                                        name="company"
                                                        value={values.company}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Company"
                                                        label="Company"
                                                        errorMessage={
                                                            errors.company
                                                        }
                                                        touchedValue={
                                                            touched.company
                                                        }
                                                    />
                                                    <TextField
                                                        type="text"
                                                        name="jobTitle"
                                                        value={values.jobTitle}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        placeholder="Job Title"
                                                        label="Job Title"
                                                        errorMessage={
                                                            errors.jobTitle
                                                        }
                                                        touchedValue={
                                                            touched.jobTitle
                                                        }
                                                    />
                                                    <button
                                                        disabled={
                                                            isSignupRequesting ||
                                                            !values.email
                                                                .length ||
                                                            !!errors.email ||
                                                            !values.firstName
                                                                .length ||
                                                            !!errors.firstName ||
                                                            !!errors.lastName ||
                                                            !!errors.company
                                                        }
                                                        key={'next-step-button'}
                                                        type="button"
                                                        onClick={switchStage}
                                                        onSubmit={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        className="form-submit__button"
                                                    >
                                                        Next Step
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <a
                                                        href="#"
                                                        onClick={switchStage}
                                                    >
                                                        {'< Go Back'}
                                                    </a>
                                                    <TextField
                                                        type="password"
                                                        name="password"
                                                        value={values.password}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        onFocus={() =>
                                                            onFocusTextField(
                                                                LOGIN_GIF.PASSWORD
                                                            )
                                                        }
                                                        placeholder="Password"
                                                        label="Password"
                                                        errorMessage={
                                                            errors.password
                                                        }
                                                        touchedValue={
                                                            touched.password
                                                        }
                                                    />
                                                    <TextField
                                                        type="password"
                                                        name="confirmPassword"
                                                        value={
                                                            values.confirmPassword
                                                        }
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        onFocus={() =>
                                                            onFocusTextField(
                                                                LOGIN_GIF.PASSWORD
                                                            )
                                                        }
                                                        placeholder="Confirm Password"
                                                        label="Confirm Password"
                                                        errorMessage={
                                                            errors.confirmPassword
                                                        }
                                                        touchedValue={
                                                            touched.confirmPassword
                                                        }
                                                    />
                                                    {!invited && (
                                                        <TextField
                                                            type="text"
                                                            name="url"
                                                            value={values.url}
                                                            onChange={
                                                                handleChange
                                                            }
                                                            onBlur={handleBlur}
                                                            placeholder="Account URL"
                                                            label="Account URL"
                                                            errorMessage={
                                                                errors.url
                                                            }
                                                            touchedValue={
                                                                touched.url
                                                            }
                                                            helperText="This is what
                                                                will preface all
                                                                your links when
                                                                sharing your
                                                                studies"
                                                        />
                                                    )}
                                                    <button
                                                        disabled={
                                                            isSignupRequesting ||
                                                            !isEmpty(errors) ||
                                                            !values.confirmPassword ||
                                                            !values.password
                                                        }
                                                        onFocus={() =>
                                                            onFocusTextField(
                                                                'log-in_success'
                                                            )
                                                        }
                                                        type="submit"
                                                        className="form-submit__button"
                                                        key={'submit-button'}
                                                    >
                                                        Create an account
                                                    </button>
                                                </>
                                            )}
                                        </Form>
                                    )}
                                />
                            )}
                            <div className="form-group form-link mt-4 text-center">
                                <Link
                                    to={LOGIN + rapidSolution}
                                    className="form-link"
                                >
                                    {`< Have an account already? Go to Login`}
                                </Link>
                            </div>
                        </div>
                        <div className="sm:block lg:hidden">
                            <Warning />
                        </div>
                    </div>
                </div>
            </div>
            <div className="page--byline hidden lg:block">
                <a
                    className="by-logo"
                    href="https://nexxt.in"
                    target="_blank"
                    rel="noreferrer"
                >
                    <img
                        src={`${STATIC_STORAGE_URL}/img/powered_by_nexxt_intelligence_inca.png`}
                    />
                </a>
            </div>
        </div>
    );
};

export default NotifyHOC(SignUp);
